// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-new/adDiscovery.{types,reducer}.ts (unchanged since f47fa8d21; TP v2 has no ad-discovery module of its own)
// (rewritten onto the platform-neutral event envelope of contracts/onboarding-session.ts; the BFF has already
// parsed the platform's own events, so the schema checks and `stream.reset` handling live there)
/**
 * Folds the ad discovery feed of one platform into one state, applying the contract's rules: events are
 * deduped by `sequence`, ads are upserted by `adId` (highest `revision` wins), and a terminal state is
 * final: later events change nothing.
 */
import type { NeutralAd, NeutralAdEvent, NeutralTerminal, StepState } from '@/contracts/onboarding-session';

/**
 * Where the live ads stand, as far as the onboarding cares:
 * - `idle`: nothing asked yet.
 * - `running`: the job is searching; ads may already be arriving.
 * - `done`: the search finished (with or without ads).
 * - `partial`: it stopped early but kept some ads.
 * - `unavailable`: no ads could be had. The onboarding carries on without them.
 */
export type LiveAdsStatus = 'idle' | 'running' | 'done' | 'partial' | 'unavailable';

export interface AdDiscoveryState {
  status: LiveAdsStatus;
  /** Highest event sequence applied; the resume cursor. */
  lastSequence: number;
  ads: Record<string, NeutralAd>;
  /** adIds in the order they arrived; a shown card never moves. */
  order: string[];
  warnings: string[];
  terminal: NeutralTerminal | null;
}

export type AdDiscoveryAction =
  /** A new run: the upstream stream restarted (`reset: true`) or the platform changed. */
  | { type: 'reset' }
  | { type: 'event'; event: NeutralAdEvent }
  /** The poll says the job ended, but no terminal event came with it. */
  | { type: 'finished'; jobStatus: StepState };

export const initialAdDiscoveryState: AdDiscoveryState = {
  status: 'idle',
  lastSequence: 0,
  ads: {},
  order: [],
  warnings: [],
  terminal: null,
};

const isTerminal = (state: AdDiscoveryState) =>
  state.status === 'done' || state.status === 'partial' || state.status === 'unavailable';

const hasAds = (state: AdDiscoveryState) => Object.keys(state.ads).length > 0;

export function adDiscoveryReducer(state: AdDiscoveryState, action: AdDiscoveryAction): AdDiscoveryState {
  switch (action.type) {
    case 'reset':
      return initialAdDiscoveryState;
    case 'finished':
      if (isTerminal(state)) return state;
      if (action.jobStatus === 'done') return { ...state, status: 'done' };
      return { ...state, status: hasAds(state) ? 'partial' : 'unavailable' };
    case 'event':
      return applyEvent(state, action.event);
  }
}

function applyEvent(state: AdDiscoveryState, event: NeutralAdEvent): AdDiscoveryState {
  if (isTerminal(state)) return state;
  if (event.sequence <= state.lastSequence && state.lastSequence > 0) return state;
  // Any event means the job is at least running.
  const next: AdDiscoveryState = {
    ...state,
    status: state.status === 'idle' ? 'running' : state.status,
    lastSequence: Math.max(state.lastSequence, event.sequence),
  };

  switch (event.type) {
    case 'ad_discovered':
    case 'ad_updated': {
      const current = state.ads[event.data.adId];
      if (current && current.revision >= event.data.revision) return next;
      return {
        ...next,
        ads: { ...state.ads, [event.data.adId]: event.data },
        order: current ? state.order : [...state.order, event.data.adId],
      };
    }
    case 'warning':
      return state.warnings.includes(event.data.code)
        ? next
        : { ...next, warnings: [...state.warnings, event.data.code] };
    case 'completed':
      return { ...next, status: 'done', terminal: event.data };
    case 'failed':
      return { ...next, status: hasAds(state) ? 'partial' : 'unavailable', terminal: event.data };
    default:
      return next;
  }
}
